import jStat from 'jstat';

export interface StatisticalSummaryResult {
  centralTendency: {
    mean: number;
    median: number;
    mode: number[];
  };
  dispersion: {
    range: number;
    variance: {
      population: number;
      sample: number;
    };
    standardDeviation: {
      population: number;
      sample: number;
    };
    iqr: number;
  };
  distribution: {
    skewness: number;
    kurtosis: number;
  };
  quartiles: {
    q1: number;
    q2: number;
    q3: number;
  };
  extremes: {
    min: number;
    max: number;
  };
  count: {
    total: number;
    unique: number;
  };
  data: number[];
  interpretation: string;
}

function calculateSkewness(data: number[]): number {
  const n = data.length;
  if (n < 3) return 0;
  
  const mean = jStat.mean(data);
  const stdDev = jStat.stdev(data, false);
  
  if (stdDev === 0) return 0;
  
  // Calculate the sum of cubed deviations
  const sumCubedDeviations = data.reduce((sum, value) => {
    const deviation = value - mean;
    return sum + Math.pow(deviation, 3);
  }, 0);
  
  // Fisher-Pearson coefficient of skewness
  return (sumCubedDeviations / n) / Math.pow(stdDev, 3);
}

function calculateKurtosis(data: number[]): number {
  const n = data.length;
  if (n < 4) return 0;
  
  const mean = jStat.mean(data);
  const stdDev = jStat.stdev(data, false);
  
  if (stdDev === 0) return 0;
  
  // Calculate the sum of fourth power of deviations
  const sumQuarticDeviations = data.reduce((sum, value) => {
    const deviation = value - mean;
    return sum + Math.pow(deviation, 4);
  }, 0);
  
  // Calculate kurtosis (Fisher's definition)
  // This returns excess kurtosis (normal distribution has kurtosis of 3, excess kurtosis of 0)
  const kurtosis = (sumQuarticDeviations / n) / Math.pow(stdDev, 4) - 3;
  
  return kurtosis + 3; // Return actual kurtosis, not excess kurtosis
}

export function calculateStatisticalSummary(data: number[]): StatisticalSummaryResult {
  if (!data || data.length === 0) {
    throw new Error('Data array is empty or undefined');
  }

  // Calculate basic statistics
  const mean = jStat.mean(data);
  const median = jStat.median(data);
  const min = jStat.min(data);
  const max = jStat.max(data);
  const range = max - min;
  const q1 = jStat.quartiles(data)[0];
  const q3 = jStat.quartiles(data)[2];
  const iqr = q3 - q1;
  const popStdDev = jStat.stdev(data, true);
  const sampleStdDev = jStat.stdev(data, false);
  const popVariance = jStat.variance(data, true);
  const sampleVariance = jStat.variance(data, false);
  const skewness = calculateSkewness(data);
  const kurtosis = calculateKurtosis(data);

  // Calculate mode
  const frequencies: { [key: number]: number } = {};
  data.forEach(value => {
    frequencies[value] = (frequencies[value] || 0) + 1;
  });
  const maxFrequency = Math.max(...Object.values(frequencies));
  const modes = Object.entries(frequencies)
    .filter(([_, freq]) => freq === maxFrequency)
    .map(([value, _]) => Number(value))
    .sort((a, b) => a - b);

  // Generate interpretation
  const distributionType = Math.abs(skewness) < 0.5 ? 'approximately symmetric' :
    skewness > 0 ? 'positively skewed' : 'negatively skewed';
  const peakedness = kurtosis > 3 ? 'more peaked' : 'flatter';

  return {
    centralTendency: {
      mean,
      median,
      mode: modes
    },
    dispersion: {
      range,
      variance: {
        population: popVariance,
        sample: sampleVariance
      },
      standardDeviation: {
        population: popStdDev,
        sample: sampleStdDev
      },
      iqr
    },
    distribution: {
      skewness,
      kurtosis
    },
    quartiles: {
      q1,
      q2: median,
      q3
    },
    extremes: {
      min,
      max
    },
    count: {
      total: data.length,
      unique: new Set(data).size
    },
    data: [...data].sort((a, b) => a - b),
    interpretation: `The data is ${distributionType} with a ${peakedness} than normal distribution. The mean (${mean.toFixed(2)}) and median (${median.toFixed(2)}) ${Math.abs(mean - median) < 0.1 * Math.max(Math.abs(mean), Math.abs(median)) ? 'are close' : 'show some difference'}, suggesting ${Math.abs(mean - median) < 0.1 * Math.max(Math.abs(mean), Math.abs(median)) ? 'relatively symmetric data' : 'potential skewness'}.`
  };
} 